import type { AreaOfUse, CrsChoice } from "./crs.ts";

export interface EpsgEntry {
  code: string;
  name: string;
  proj4: string;
  /** [north, west, south, east] — that specific order, not the more common [west, south, east, north]. */
  bbox: [number, number, number, number] | null;
}

interface RawEntry {
  code: string;
  kind: string;
  name: string;
  proj4?: string | null;
  bbox?: [number, number, number, number] | null;
}

// Only 2D horizontal systems make sense for a [COORDINATES] pair — vertical,
// compound, geocentric and local-engineering CRS kinds are excluded.
const USABLE_KINDS = new Set(["CRS-PROJCRS", "CRS-GEOGCRS"]);

let cache: Promise<EpsgEntry[]> | null = null;

function loadAll(): Promise<EpsgEntry[]> {
  cache ??= import("epsg-index/all.json").then((module) => {
    // `unknown` here widens a type that doesn't structurally match — the
    // filter below still checks shape, this isn't a way to skip validation.
    const raw = module.default as unknown as Record<string, RawEntry>;
    return Object.values(raw)
      .filter((entry): entry is RawEntry & { proj4: string } => {
        return USABLE_KINDS.has(entry.kind) && typeof entry.proj4 === "string";
      })
      .map((entry) => ({
        code: entry.code,
        name: entry.name,
        proj4: entry.proj4,
        bbox: entry.bbox ?? null,
      }));
  });
  return cache;
}

interface SearchableEntry {
  entry: EpsgEntry;
  haystack: string;
}

let searchCache: Promise<SearchableEntry[]> | null = null;

function loadSearchable(): Promise<SearchableEntry[]> {
  searchCache ??= loadAll().then((all) =>
    all.map((entry) => ({ entry, haystack: `${entry.code} ${entry.name}`.toLowerCase() }))
  );
  return searchCache;
}

/** Safe to call more than once — only the first call does any work. */
export function preloadEpsg(): void {
  void loadSearchable();
}

/** Every whitespace-separated word in the query must appear SOMEWHERE in the code+name, independently — not as one contiguous phrase — so "ETRS89 30N" finds "ETRS89 / UTM zone 30N" even though "etrs89 30n" is never a substring of it. */
export async function searchEpsg(query: string, limit = 40): Promise<EpsgEntry[]> {
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return [];
  const all = await loadSearchable();
  const matches: EpsgEntry[] = [];
  for (const { entry, haystack } of all) {
    if (words.every((word) => haystack.includes(word))) {
      matches.push(entry);
      if (matches.length >= limit) break;
    }
  }
  return matches;
}

export async function findEpsgByCode(code: string): Promise<EpsgEntry | null> {
  const all = await loadAll();
  return all.find((entry) => entry.code === code) ?? null;
}

export function toCrsChoice(entry: EpsgEntry): CrsChoice {
  const areaOfUse: AreaOfUse | undefined = entry.bbox
    ? { north: entry.bbox[0], west: entry.bbox[1], south: entry.bbox[2], east: entry.bbox[3] }
    : undefined;
  return { proj4: entry.proj4, label: `${entry.name} — EPSG:${entry.code}`, areaOfUse };
}
